import { useEffect, useState } from "react";
import {
  authConfigured, currentUser, onSessionChange, signInWithMicrosoft, signOut,
  type SessionUser,
} from "../session.js";

const MicrosoftMark = () => (
  <svg viewBox="0 0 23 23" width="16" height="16" aria-hidden="true">
    <rect x="1" y="1" width="10" height="10" fill="#f25022" />
    <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
    <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
    <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
  </svg>
);

/**
 * The gate.
 *
 * Nothing behind it renders until there is a session, because the server
 * refuses an anonymous caller outright — a board that loaded and then failed
 * every call would read as broken rather than as locked.
 */
export function SignInGate() {
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <div className="gate">
      <div className="gate-card">
        <h1>Engineering<br />Scheduling</h1>
        <p className="gate-note">Sign in with your work account to continue.</p>
        <button
          className="btn sso gate-sso"
          disabled={busy}
          onClick={async () => {
            setErr(null);
            setBusy(true);
            try {
              await signInWithMicrosoft();
            } catch (e) {
              setErr((e as Error).message);
              setBusy(false);
            }
          }}
        >
          <MicrosoftMark />
          {busy ? "Redirecting…" : "Continue with Microsoft"}
        </button>
        {err && <div className="signin-err">{err}</div>}
        <div className="gate-foot">
          Everyone who signs in can see the whole schedule. Changing it depends on your
          role, which an admin sets on the People screen.
        </div>
      </div>
    </div>
  );
}

/** Who you are, and what you may do — in the topbar, where you notice it. */
export function SessionChip({ role, onChange }: { role: string | undefined; onChange: () => void }) {
  const [user, setUser] = useState<SessionUser | null>(currentUser());
  useEffect(() => onSessionChange(setUser), []);

  return (
    <>
      <span className="chip" title="Your access role. An admin sets this on the People screen.">
        {role ?? "…"}
      </span>
      {user && <span className="chip" title={user.email ?? ""}>{user.email ?? "signed in"}</span>}
      {authConfigured && user && (
        <button className="btn" onClick={async () => { await signOut(); onChange(); }}>
          Sign out
        </button>
      )}
    </>
  );
}
